import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Tab, Tabs } from '@mui/material';

import { ErrorState, PageContainer } from '@/shared/ui';

import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { PublicationCatalogTypesPanel } from '../components/PublicationCatalogTypesPanel';
import { PublicationCatalogsPanel } from '../components/PublicationCatalogsPanel';
import { PublicationChannelsPanel } from '../components/PublicationChannelsPanel';

type TabKey = 'channels' | 'catalogs' | 'catalogTypes';

/**
 * Publication workspace — sales channels + catalog bindings, publication catalogs (with their
 * lifecycle), and the catalog-type publication-profile registry (epic-catalog-001,
 * user-story-merchant-portal-034/035/036). Tabbed like `PricingPage`.
 */
export function PublicationPage() {
  const { t } = useTranslation('catalog');
  const { canPublicationView, canPublicationManage } = useCatalogPermissions();
  const [tab, setTab] = useState<TabKey>('channels');

  if (!canPublicationView) {
    return (
      <PageContainer title={t('publicationWorkspace.title')}>
        <ErrorState
          title={t('publicationWorkspace.accessRestricted.title')}
          message={t('publicationWorkspace.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t('publicationWorkspace.title')} subtitle={t('publicationWorkspace.subtitle')}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_e, value: TabKey) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="channels" label={t('publicationWorkspace.tabs.channels')} />
          <Tab value="catalogs" label={t('publicationWorkspace.tabs.catalogs')} />
          <Tab value="catalogTypes" label={t('publicationWorkspace.tabs.catalogTypes')} />
        </Tabs>
      </Box>

      {tab === 'channels' ? <PublicationChannelsPanel canManage={canPublicationManage} /> : null}
      {tab === 'catalogs' ? <PublicationCatalogsPanel canManage={canPublicationManage} /> : null}
      {tab === 'catalogTypes' ? <PublicationCatalogTypesPanel canManage={canPublicationManage} /> : null}
    </PageContainer>
  );
}
